import { Code, type CallOptions, type Client } from "@connectrpc/connect";
import type { Account, FeatureType, Subscription } from "#gen/billing/v1/billing_pb.js";
import { BillingService, WindowSize, type GetSubscriptionStateResponse, type ListInvoicesResponse, type UsageSummary } from "#gen/billing/v1/service_pb.js";
import type { PaginationArguments } from "#gen/common/v1/pagination_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { APIError, handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";

export class BillingClient {
	private client: Client<typeof BillingService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.billing || "/", BillingService, options);
	}

	async getSubscriptionState(orgId: string): Promise<GetSubscriptionStateResponse> {
		try {
			return await this.client.getSubscriptionState({ entityAccountId: orgId });
		} catch (e) {
			throw handleError(e);
		}
	}

	async getSubscriptions(orgId: string): Promise<Subscription[]> {
		try {
			const { subscriptions } = await this.client.listSubscriptions({ entityAccountId: orgId });
			return subscriptions;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getAccount(orgId: string): Promise<Account | undefined> {
		try {
			const { account } = await this.client.getAccount({ entityAccountId: orgId });
			return account;
		} catch (e) {
			if ((e instanceof APIError && e.code === Code.NotFound) || (e instanceof Error && e.message.includes("not found"))) {
				return undefined;
			}
			throw handleError(e);
		}
	}

	async listInvoices(orgId: string, paginate?: PaginationArguments, options?: CallOptions): Promise<ListInvoicesResponse> {
		try {
			return await this.client.listInvoices({ entityAccountId: orgId, paginate }, options);
		} catch (e) {
			throw handleError(e);
		}
	}

	async downloadInvoice(orgId: string, invoiceNum: string): Promise<string> {
		try {
			const { invoiceDownloadUrl } = await this.client.downloadInvoicePDF({ entityAccountId: orgId, invoiceNumber: invoiceNum });
			return invoiceDownloadUrl;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getUsageSummary(entityAccountId: string, featureType: FeatureType, windowSize: WindowSize = WindowSize.DAY): Promise<UsageSummary | undefined> {
		try {
			const { usageSummary } = await this.client.getUsageSummary({
				entityAccountId,
				featureType,
				windowSize,
			});
			return usageSummary;
		} catch (e) {
			throw handleError(e);
		}
	}
}
